<template>
  <div>
    <toastification-loading-content ref="loadingToast" />

    <b-row v-if="showFilters" class="pt-2 pb-1">
      <b-col v-if="!embedded" cols="6" class="d-flex align-items-center">
        <BreadcrumbMenu />
      </b-col>
     <b-col :cols="embedded ? 12 : 6" class="pr-3 text-right">
        <button-filters
          :modal-id="filterModalId"
          @show-modal="$root.$emit('bv::show::modal', $event)"
          :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters"
        />
      </b-col>
    </b-row>

    <!-- Filters -->
    <filter-modal
      v-if="showFilters"
      :modal-id="filterModalId"
      @action="buildFilters()"
      @clear="clearSearchFilters()"
    >
      <template v-slot:main-filters>
        <filters
          :dvr-filter.sync="dvrFilter"
          :dvr-options="dvrOptions"
          :type-log-filter.sync="typeLogFilter"
          :type-log-options="typeLogOptions"
          :type-filter.sync="typeFilter"
          :type-options="typeOptions"
          :camera-filter.sync="cameraFilter"
          :camera-options="cameraOptions"
          :start-datetime.sync="startDatetimeFilter"
          :end-datetime.sync="endDatetimeFilter"
        />
      </template>
      <template v-slot:footer-buttons>
        <!-- <b-button
          variant="secondary"
          class="float-right text-capitalize"
          size="sm"
          :disabled="buttonUpdate"
          @click="updateCameras()"
        >
          {{ $t("actions.update") + " " + $tc("hanwha_camera.title", 1) }}
        </b-button> -->
      </template>
    </filter-modal>

    <Transition name="slide-right-left">
      <div v-if="embedded && isFilterPanelOpen" class="filters-panel-fullscreen">
        <div class="filters-panel-header">
          <p class="h4 text-capitalize mb-2 text-white">
            {{ $t("messages.info.advanced_search") }}
          </p>
          <b-button variant="link" size="sm" class="text-dark p-0" @click="isFilterPanelOpen = false">
            <feather-icon icon="XIcon" size="24" />
          </b-button>
        </div>
        <div class="filters-panel-content">
          <filters
            :dvr-filter.sync="dvrFilter"
            :dvr-options="dvrOptions"
            :type-log-filter.sync="typeLogFilter"
            :type-log-options="typeLogOptions"
            :type-filter.sync="typeFilter"
            :type-options="typeOptions"
            :camera-filter.sync="cameraFilter"
            :camera-options="cameraOptions"
            :start-datetime.sync="startDatetimeFilter"
            :end-datetime.sync="endDatetimeFilter"
          />
        </div>
        <div class="filters-panel-footer">
          <b-button variant="outline-primary" size="sm" class="text-capitalize" @click="clearEmbeddedFilters">
            {{ $t("messages.info.clear_filters") }}
          </b-button>
          <b-button variant="primary" size="sm" class="text-capitalize" @click="applyEmbeddedFilters">
            {{ $t("search") }} <feather-icon icon="SearchIcon" />
          </b-button>
        </div>
      </div>
    </Transition>

    <!-- Table Container Card -->
    <alarms-logs-component
      v-show="!embedded || !isFilterPanelOpen"
      ref="refListTable"
      :current-page="currentPage"
      :data-meta="dataMeta"
      :fetch-list="fetchList"
      :is-sort-dir-desc="isSortDirDesc"
      :per-page="perPage"
      :per-page-options="perPageOptions"
      :search-query="searchQuery"
      :show-filters="showFilters"
      :show-export="true"
      :exporting="exporting"
      :embedded="embedded"
      :sort-by="sortBy"
      :table-columns="tableColumns"
      :total-list="totalList"
      @per-page="perPage = $event"
      @update="update"
      @refresh-data="refetchData"
      @export="exportList"
    >
      <template #search>
        <b-form-input
          v-model="searchQuery"
          class="d-inline-block mr-1 hanwha-logs-search-input"
          :placeholder="$t('table.search')"
        />
        <b-button-group v-if="embedded" class="mr-1">
          <b-button class="buttonFilter cursor-pointer d-flex align-items-center" @click="isFilterPanelOpen = true">
            <b-icon-filter-circle-fill scale="1.1" />
            <span class="align-middle ml-1 text-capitalize">{{ $t("filters") }}</span>
          </b-button>
          <toggle-clear-filter :filters-count="filtersCount" @clear-search-filters="clearSearchFilters" />
        </b-button-group>
      </template>
      <template #cell-dvr="{ item, value }">
        <div>{{ value }}</div>
        <small v-if="item.camera && item.camera.name" class="text-muted">
          {{ item.camera.name }}
        </small>
      </template>
    </alarms-logs-component>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BFormInput,
  BButton,
  BButtonGroup,
} from "bootstrap-vue";
import { onMounted, ref, watch } from "@vue/composition-api";
import useList from "./useList";
import Filters from "./Filters.vue";
import ToastificationLoadingContent from "@core/components/toastification/ToastificationLoadingContent.vue";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import FilterModal from "@/components/Filters/FilterModal.vue";
import ButtonFilters from "@/components/Filters/ButtonFilters.vue";
import AlarmsLogsComponent from "@/views/dmp/components/AlarmsLogsComponent.vue";
import ToggleClearFilter from "@/components/Filters/ToggleClearFilter.vue";

export default {
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
    showFilters: {
      type: Boolean,
      default: true,
    },
    slotId: {
      type: String,
      default: null,
    },
    slotFilters: {
      type: Object | Array,
      default: () => ({}),
    },
  },
  components: {
    BreadcrumbMenu,
    Filters,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BButtonGroup,
    ToastificationLoadingContent,
    FilterModal,
    ButtonFilters,
    AlarmsLogsComponent,
    ToggleClearFilter,
  },

  computed: {
    filterModalId() {
      return this.embedded
        ? `hanwha-logs-filter-modal-${this.slotId || this._uid}`
        : "filter_modal";
    },
  },

  setup(props) {
    const isFilterPanelOpen = ref(false);
    //HOOKS
    onMounted(() => connectSocket());

    //COMPOSABLES
    const {
      currentPage,
      dvrFilter,
      typeLogFilter,
      typeFilter,
      cameraFilter,
      startDatetimeFilter,
      endDatetimeFilter,
      filters,
      filtersCount,
      isSortDirDesc,
      perPage,
      refListTable,
      tableColumns,
      totalList,
      perPageOptions,
      searchQuery,
      sortBy,

      dataMeta,
      dvrOptions,
      typeLogOptions,
      typeOptions,
      cameraOptions,

      buildFilters,
      clearSearchFilters,
      fetchList,
      exportList,
      exporting,
      refetchData
    } = useList();

    let slotFiltersApplied = false;
    watch(
      [
        () => props.slotFilters?.filters ?? props.slotFilters,
        dvrOptions,
      ],
      ([slotFilters]) => {
        if (Object.prototype.hasOwnProperty.call(slotFilters || {}, 'startDatetime')) {
          startDatetimeFilter.value = slotFilters.startDatetime;
        }
        if (Object.prototype.hasOwnProperty.call(slotFilters || {}, 'endDatetime')) {
          endDatetimeFilter.value = slotFilters.endDatetime;
        }
        typeLogFilter.value = slotFilters?.typeLogFilter || [];
        typeFilter.value = slotFilters?.typeFilter || [];

        const unitIds = (slotFilters?.unitIds || [slotFilters?.unit])
          .filter(id => id != null)
          .map(String);

        if (unitIds.length) {

          const matchingDvrIds = dvrOptions.value
            .filter(option => unitIds.includes(String(option.id_unit)))
            .map(option => option.value);

          // An impossible ID avoids displaying logs from every installation
          // when the focused installation has no Hanwha DVR.
          dvrFilter.value = matchingDvrIds.length ? matchingDvrIds : [-1];
        } else {
          dvrFilter.value = slotFilters?.dvrFilter?.length ? slotFilters.dvrFilter : null;
        }

        filters.value = {
          dvr: dvrFilter.value,
          type_log: typeLogFilter.value,
          type: typeFilter.value,
          camera: cameraFilter.value,
          start_date: startDatetimeFilter.value,
          end_date: endDatetimeFilter.value,
        };

        if (slotFiltersApplied) refetchData();
        slotFiltersApplied = true;
      },
      {
        immediate: true,
        deep: true,
      }
    );

    //METHODS
    const connectSocket = () => {
      window.Echo.private('hanwha-logs')
        .listen('.hanwha-logs-checking', (event) => {
          console.log('event', event);
        });
    }

    const update = (data) => {
      currentPage.value = data;
    }

    const applyEmbeddedFilters = () => {
      buildFilters();
      isFilterPanelOpen.value = false;
    };

    const clearEmbeddedFilters = () => {
      clearSearchFilters();
      isFilterPanelOpen.value = false;
    };

    return {
      currentPage,
      dvrFilter,
      typeLogFilter,
      typeFilter,
      cameraFilter,
      startDatetimeFilter,
      endDatetimeFilter,
      filters,
      filtersCount,
      isSortDirDesc,
      perPage,
      refListTable,
      tableColumns,
      totalList,
      perPageOptions,
      searchQuery,
      sortBy,

      dataMeta,
      dvrOptions,
      typeLogOptions,
      typeOptions,
      cameraOptions,

      buildFilters,
      clearSearchFilters,
      fetchList,
      exportList,
      exporting,
      refetchData,
      update,
      isFilterPanelOpen,
      applyEmbeddedFilters,
      clearEmbeddedFilters,
    };
  },
};
</script>

<style scoped lang="scss">
.hanwha-logs-search-input {
  flex: 1 1 0;
  min-width: 120px;
}

.filters-panel-fullscreen {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  background-color: white;
  z-index: 10;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.filters-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 1.5rem 0;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.filters-panel-content {
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.5rem;
}

.filters-panel-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e9ecef;
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  flex-shrink: 0;
}

.dark-layout {
  .filters-panel-fullscreen {
    background-color: #283046;
    color: #b4b7bd;
  }

  .filters-panel-header {
    border-bottom-color: rgba(255, 255, 255, 0.05);
  }

  .filters-panel-footer {
    border-top-color: #3b4253;
  }
}
</style>
